
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';

import SponsoredRecommendation from '@/components/sponsored-recommendations/SponsoredRecommendation.vue';
import HorizontalTitleListGraphql from '@/components/timeline/HorizontalTitleListGraphql.vue';

import { ImpressionTrackingEvents } from '@/enums/events';

import { TrackingProviderPropertiesInterface } from '@/helpers/tracking/providers';
import { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import type { TitleDetailSeason, TitleDetail } from '@/interfaces/title-details-graphql';
import { SimilarTitleFragment } from '@/components/graphql/queries/GetSimilarTitles.query';
import type { SponsoredAdFragment } from '@/pages/graphql/fragments/SponsoredAd.fragment';
import { trackTitleDetailRedesign } from '@/pages/experiments/title-detail-redesign/tracking';

@Component<SimilarTitlesGraphql>({
	name: 'SimilarTitlesGraphql',
	components: { HorizontalTitleListGraphql, SponsoredRecommendation },
})
export default class SimilarTitlesGraphql extends Vue {
	@Prop({ required: true }) titles: SimilarTitleFragment[];
	@Prop({ required: true }) title: NonNullable<TitleDetail | TitleDetailSeason['show']>;
	@Prop({ default: null }) sponsoredAd: SponsoredAdFragment | null;
	@Prop({ default: () => [] }) additionalContexts: SnowplowContext[];
	@Prop({ default: '' }) impressionTrackingLabel: string;
	@Prop({ default: '' }) impressionTrackingProperty: string;

	@Watch('title.id')
	onTitleIdChange(newTitleId: string, oldTitleId: string) {
		if (process.client && newTitleId !== oldTitleId) {
			this.trackSimilarTitlesLoaded();
		}
	}

	get impressionTrackingCategory() {
		return ImpressionTrackingEvents.SIMILAR_TITLES;
	}

	created() {
		// Note(valerio.mazza): SSR special case we need to track the Event on created
		if (process.server && this.titles?.length) {
			this.trackSimilarTitlesLoaded();
		}
	}

	trackSimilarTitlesLoaded() {
		const properties: TrackingProviderPropertiesInterface = { action: 'loaded', nonInteraction: true };
		if (this.impressionTrackingLabel) properties.label = this.impressionTrackingLabel;

		TrackingHelper.trackEvent(ImpressionTrackingEvents.SIMILAR_TITLES, properties, this.additionalContexts);
	}

	trackSimilarTitlesClicked({ index, title }: { index: number; title: SimilarTitleFragment }) {
		let property: string = ImpressionTrackingEvents.SIMILAR_TITLES;
		if (this.impressionTrackingProperty) property = this.impressionTrackingProperty;

		TrackingHelper.trackEvent('userinteraction', {
			action: 'title_clicked',
			label: `${this.title.id},${title.id ?? ''}`,
			value: index,
			property,
		});
		//TITLE_DETAIL_REDESIGN
		trackTitleDetailRedesign({ action: 'click', label: 'title_clicked' });
		//TITLE_DETAIL_REDESIGN
	}

	trackScroll(label: string) {
		const properties: TrackingProviderPropertiesInterface = { action: 'horizontal_scroller_clicked', label };
		if (this.impressionTrackingLabel) properties.property = this.impressionTrackingLabel;

		TrackingHelper.trackEvent('userinteraction', properties, this.additionalContexts);
		trackTitleDetailRedesign({ action: 'click', label: 'horizontal_scroller_clicked' });
	}
}
